import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import {
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  SelectChangeEvent,
  Skeleton,
  Stack,
  useMediaQuery
} from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import {
  mobileWidth,
  ProgressButton,
  ConfirmationModal,
  formatDayjsMaybe,
  globalDateFormat,
  useToast,
  SquareIconButton
} from "@qubit/autoparts";

import dayjs, { Dayjs } from "dayjs";
import {
  ChangeEvent,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { useBarcodeScanner, useKeyDownHandler } from "~/lib/barCodeScan";
import {
  inventoryDateLabel,
  isExpirationValid,
  getInventoryDateObj,
  checkIsExpiration,
  getHoldTypeOptions,
  holdTypeCheck,
  isAutostoreView,
  HoldType,
  HoldTypeSource,
  runAsyncSafely,
  getBarcodeValue,
  getMinInventoryExpirationDate
} from "~/lib/helpers";
import {
  mixpanelTrack,
  mixpanelTrackModalDismiss,
  useMixpanelModalName
} from "~/lib/mixpanel-tracking";
import { QUANTITY_MAX_VALUE, LOT_NUMBER_MAX_LENGTH } from "~/lib/shared";
import {
  adjustInventory,
  generateInventoryReasonCodeOptions,
  clearInventoryToModify,
  getVariantData,
  placeInventoryHold,
  findInventoryByAutostoreBinNumber,
  deleteInventoryHold
} from "~/redux/actions/inventory";
import { InventoryHoldReasonCode } from "~/redux/public/inventory.openApi";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import {
  selectSelectedPortId,
  selectThisWorkstation,
  selectWorkstationAutostoreGridId
} from "~/redux/selectors/workstationsSelectors";
import {
  VariantFrontendDto,
  InventorySummaryDto,
  HoldDto,
  MeasuredValueDto
} from "~/types/api";

export type InventoryToModify = {
  binType: string;
  binId: Guid;
  inventoryId: Guid;
  locationName?: string;
  count?: MeasuredValueDto;
  committedCount?: MeasuredValueDto;
  expiration?: Date | string;
  manufactureDate?: string;
  lotNumber: string;
  variantId: Guid;
  hold?: string;
  holds?: HoldDto[];
} | null;

const connector = connect(null, {
  adjustInventory,
  getVariantData,
  placeInventoryHold,
  findInventoryByAutostoreBinNumber,
  deleteInventoryHold
});

type PropsFromRedux = ConnectedProps<typeof connector>;

type InventoryAdjustDialogProps = PropsFromRedux & {
  selectedVariant: VariantFrontendDto | null;
  refreshCb: ({
    inventoryWasEmptied,
    newQuantity,
    inventoryId,
    newDate
  }: {
    inventoryWasEmptied?: boolean;
    newQuantity?: number;
    inventoryId?: Guid;
    newDate?: Date;
  }) => void;
  isCycleCountBlind?: boolean;
  // should be moved into a function that returns the hook & state?
  // maybe make a function that takes in parameters that can alter the state and execute the hook.
  hasInventoryAdjusted?: boolean;
  setHasInventoryAdjusted?: Dispatch<SetStateAction<boolean>>;
  onClose?: () => void;
  disabled?: boolean;
  invToModify: InventoryToModify;
  canceledReason: string;
  numberOfCompartments?: number | null;
  currentSelectedPortId?: number;
  isAdjustOnMain?: boolean;
  setCurrentSelectedInventoryRecord?: (
    selectedInventoryRecord: InventorySummaryDto
  ) => void;
  open: boolean;
  children?: ReactNode;
};

/**
 * An inventory adjust dialog shared across different pages.
 */
export function InventoryAdjustDialog(props: InventoryAdjustDialogProps) {
  const isMobile = useMediaQuery(mobileWidth);
  const trackedModalName = useMixpanelModalName("Adjust Inventory");
  const { t } = useTranslation();
  const {
    invToModify,
    selectedVariant,
    disabled = false,
    onClose,
    isCycleCountBlind,
    hasInventoryAdjusted,
    setHasInventoryAdjusted,
    numberOfCompartments,
    currentSelectedPortId,
    isAdjustOnMain,
    setCurrentSelectedInventoryRecord,
    open,
    children
  } = props;

  const { successToast, errorToast } = useToast();
  const dispatch = useAppDispatch();
  const selectedSummaries = useAppSelector(
    (state) => state.inventoryNew.selectedSummaries
  );
  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const selectedAutostoreGridId = useAppSelector(
    selectWorkstationAutostoreGridId
  );
  const selectedPortId = useAppSelector(selectSelectedPortId);

  const {
    inv_inventoryDateLabel,
    inv_inventoryDateRequired,
    ap_excludeRecalledHold
  } = useAppSelector(selectServerConfig);

  const updatedInventoryToModify = useAppSelector(
    (state) => state.inventory.inventoryToModify
  );

  const updatedInventoryLoading = useAppSelector(
    (state) => state.inventory.loadingInvToModify
  );

  const workstation = useAppSelector(selectThisWorkstation);

  const [quantity, updateQuantity] = useState<number | null>(null);
  const [unitOfMeasure, updateUOM] = useState<string | null>(null);
  const [reasonCode, updateReasonCode] = useState<string>("Counted");
  const [inventoryDate, setInventoryDate] = useState<Dayjs | null>(null);
  const [updatedLotNumber, setUpdatedLotNumber] = useState<string | null>(null);
  const [transferOutLocation, setTransferOutLocation] = useState<string | null>(
    null
  );
  const [areInventoryFieldsDirty, setAreInventoryFieldsDirty] = useState(false);
  const [areHoldFieldsDirty, setAreHoldFieldsDirty] = useState(false);

  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isAdjustBtnDisabled, setIsAdjustBtnDisabled] = useState(false);

  const [availableHoldTypes, setAvailableHoldTypes] = useState<HoldType[]>([]);
  const [currentHoldTypes, setCurrentHoldTypes] = useState<HoldType[]>([]);

  const [expanded, setExpanded] = useState(open && !!currentHoldTypes.length);

  const isCycleCountsPage = useLocation().pathname.includes(
    "autostore-cyclecount"
  );

  // Do not adjust inventory on cycle counts v2.
  // For v2, the adjustment is handled on the backend.
  const canAdjustInventory = !useLocation().pathname.includes(
    "autostore-cyclecountv2"
  );

  const isAutostoreInventory = useLocation().pathname.includes(
    "autostore-inventory"
  );

  type ConfirmText = "confirm" | "confirm and adjust inventory" | "next bin";
  let confirmText: ConfirmText = "confirm";
  if (isCycleCountsPage && (isCycleCountBlind || areInventoryFieldsDirty)) {
    confirmText = "confirm and adjust inventory";
  } else if (isAutostoreInventory) {
    confirmText =
      areInventoryFieldsDirty ||
      areHoldFieldsDirty ||
      selectedSummaries?.length === 1
        ? "confirm"
        : "next bin";
  }

  const holdTypes = getHoldTypeOptions({
    excludeRecalledHold: ap_excludeRecalledHold
  });

  let quantityToDisplay: string | number = "";

  // set the display qty to the quantity or to the set value above.
  quantityToDisplay =
    typeof quantity === "number" ? quantity : quantityToDisplay;

  const clientFCReasonCodes =
    usersFulfillmentCenter?.clientConfiguration
      ?.clientSpecificInventoryAdjustmentReasonCodes;

  const isInputDateValid = isExpirationValid(
    inv_inventoryDateRequired || selectedVariant?.isExpirationRequired,
    inventoryDate
  );
  const hasUserClearedDate =
    !inventoryDate && !!updatedInventoryToModify?.expiration;
  const isDateFieldError =
    quantity !== 0 && (!isInputDateValid || hasUserClearedDate);

  const isExpirationDate = checkIsExpiration(inv_inventoryDateLabel);

  useKeyDownHandler();
  useBarcodeScanner({
    disabled: !open,
    findScanMatch: (buffer: string) => {
      if (reasonCode.toLowerCase() !== "transfer") {
        return false;
      }
      const barcodeValue = getBarcodeValue(buffer);
      setTransferOutLocation(barcodeValue);
      return true;
    }
  });

  // default holds section to expanded if selected inventory has holds
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-derived-state
    if (open && currentHoldTypes.length) setExpanded(true);
  }, [currentHoldTypes, open]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-chain-state-updates, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
    if (open) setIsAdjustBtnDisabled(!isInputDateValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedInventoryToModify?.inventoryId, inventoryDate, open]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (open && invToModify) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      if (updatedInventoryToModify?.count && !isCycleCountBlind) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
        updateQuantity(updatedInventoryToModify?.count.value);
      }
      if (updatedInventoryToModify?.lotNumber) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
        setUpdatedLotNumber(updatedInventoryToModify.lotNumber);
      }
      if (isExpirationDate) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
        setInventoryDate(
          updatedInventoryToModify?.expiration
            ? dayjs(updatedInventoryToModify.expiration)
            : null
        );
      } else if (!isExpirationDate) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
        setInventoryDate(
          updatedInventoryToModify?.manufactureDate
            ? dayjs(updatedInventoryToModify.manufactureDate)
            : null
        );
      }
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      updateUOM(updatedInventoryToModify?.count?.units || null);

      // sets the currentSelectedInventory in parent component to update the bin components selected compartment
      if (updatedInventoryToModify) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (setCurrentSelectedInventoryRecord)
          // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
          setCurrentSelectedInventoryRecord(updatedInventoryToModify);
      }
    }

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!invToModify) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      updateQuantity(0);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      updateReasonCode("Counted");
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setInventoryDate(null);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      updateUOM(null);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setTransferOutLocation(null);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setAreInventoryFieldsDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    invToModify?.inventoryId,
    updatedInventoryToModify?.inventoryId,
    updatedInventoryToModify?.count?.value,
    updatedInventoryToModify?.expiration,
    updatedInventoryToModify?.manufactureDate,
    updatedInventoryToModify?.lotNumber,
    open
  ]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (open) {
      void props.getVariantData({
        variantId: invToModify?.variantId,
        binId: invToModify?.binId,
        limit: 50,
        offset: 0,
        autostoreGridId: isAutostoreView(window.location.search)
          ? selectedAutostoreGridId
          : undefined,
        inventoryId: invToModify?.inventoryId
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchCurrentHolds = async () => {
    return runAsyncSafely(async () => {
      let inventory = null;
      if (
        selectedAutostoreGridId &&
        updatedInventoryToModify?.autostoreBinNumber
      ) {
        inventory = await props.findInventoryByAutostoreBinNumber(
          selectedAutostoreGridId,
          updatedInventoryToModify?.autostoreBinNumber
        );
      }

      if (inventory && inventory.length > 0) {
        const mergedCurrentHolds = inventory
          .filter(
            (item) =>
              item.bin.autostoreCompartmentNumber ===
              updatedInventoryToModify?.autostoreCompartmentNumber
          )
          .flatMap((item) =>
            item.holds.map((hold) => ({
              reasonCode: hold.reasonCode as InventoryHoldReasonCode,
              source: hold.source as HoldTypeSource
            }))
          );
        // Rest of the code remains the same
        setAvailableHoldTypes(mergedCurrentHolds);
        setCurrentHoldTypes(mergedCurrentHolds);
      }
    });
  };
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (updatedInventoryToModify?.inventoryId && open) {
      void fetchCurrentHolds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedInventoryToModify?.inventoryId, open]);

  const setInventoryDirty = () => {
    setAreInventoryFieldsDirty(true);
    if (setHasInventoryAdjusted) setHasInventoryAdjusted(true);
  };

  const handleQuantityChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setInventoryDirty();
    // allows single 0, with no trailing values, no special chars, a single white space.
    const validQuantity = /^(?:(?:[1-9]\d*)|0)?\s*$/.test(event.target.value);

    if (validQuantity) {
      if (event.target.value === "") {
        setIsAdjustBtnDisabled(true);
        updateQuantity(null);
        return;
      }
      const newValue = event.target.value ? Number(event.target.value) : null;
      if (newValue !== null && newValue > QUANTITY_MAX_VALUE) {
        return;
      }
      updateQuantity(newValue);
      setIsAdjustBtnDisabled(false);
    }
  };

  const handleReasonCodeChange = (event: SelectChangeEvent<unknown>) => {
    if (typeof event?.target?.value === "string") {
      mixpanelTrack({
        trackedPageName: trackedModalName,
        type: "Select Option",
        label: "Reason Code",
        eventProperties: {
          "New Value": event.target.value
        }
      });
      setInventoryDirty();
      updateReasonCode(event.target.value);
    }
  };

  const handleAddHold = async (newReasonCode: string): Promise<void> => {
    if (newReasonCode && workstation && updatedInventoryToModify?.inventoryId) {
      await props.placeInventoryHold(
        updatedInventoryToModify.inventoryId,
        newReasonCode,
        workstation.id
      );
    }
  };

  const handleRemoveHold = async (oldReasonCode: string): Promise<void> => {
    if (
      oldReasonCode &&
      workstation &&
      updatedInventoryToModify?.inventoryId &&
      selectedAutostoreGridId &&
      selectedPortId
    ) {
      await props.deleteInventoryHold(
        updatedInventoryToModify.inventoryId,
        workstation.id,
        oldReasonCode,
        selectedAutostoreGridId,
        selectedPortId
      );
      successToast(t("inventory hold removed"));
    }
  };

  const closeDialog = () => {
    setAreInventoryFieldsDirty(false);
    setAreHoldFieldsDirty(false);
    setUpdatedLotNumber(null);
    setTransferOutLocation(null);
    dispatch(clearInventoryToModify());
    if (onClose) onClose();
  };

  const handleConfirmClick = async (clearInventory = false): Promise<void> => {
    const updatedQty = clearInventory ? 0 : quantity;
    if (isCycleCountBlind && quantityToDisplay === "") {
      return;
    }
    if (
      canAdjustInventory &&
      (clearInventory || areInventoryFieldsDirty) &&
      updatedInventoryToModify
    ) {
      const { inventoryId, lotNumber } = updatedInventoryToModify;
      await props.adjustInventory({
        autostoreGridId: selectedAutostoreGridId,
        autostorePortId: currentSelectedPortId || selectedPortId || undefined,
        inventoryId,
        count: {
          units: updatedInventoryToModify.count?.units || "",
          value: updatedQty ?? (updatedInventoryToModify.count?.value as number)
        },
        lotNumber: updatedLotNumber || lotNumber,
        ...getInventoryDateObj(inv_inventoryDateLabel, inventoryDate),
        reasonCode,
        workstationId: workstation?.id,
        details: transferOutLocation || undefined
      });
      mixpanelTrack({
        trackedPageName: trackedModalName,
        type: "Button Click",
        label: "Adjust Inventory Confirm",
        eventProperties: {
          "Inventory ID": inventoryId,
          Quantity: updatedQty ?? updatedInventoryToModify.count?.value,
          "Reason Code": reasonCode
        }
      });
    }

    if (areHoldFieldsDirty && updatedQty !== 0) {
      const currentHoldTypeCodes = currentHoldTypes.map((c) => c.reasonCode);
      const availableHoldTypeCodes = availableHoldTypes.map(
        (a) => a.reasonCode
      );

      for (const cht of currentHoldTypeCodes) {
        if (!availableHoldTypeCodes.includes(cht)) {
          await handleAddHold(cht);
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Button Click",
            label: "Add Inventory Hold Confirm",
            eventProperties: {
              "Inventory ID": updatedInventoryToModify?.inventoryId,
              "Reason Code": cht
            }
          });
        }
      }
      for (const aht of availableHoldTypeCodes) {
        if (!currentHoldTypeCodes.includes(aht)) {
          try {
            await handleRemoveHold(aht);
            mixpanelTrack({
              trackedPageName: trackedModalName,
              type: "Button Click",
              label: "Remove Inventory Hold Confirm",
              eventProperties: {
                "Inventory ID": updatedInventoryToModify?.inventoryId,
                "Reason Code": aht
              }
            });
          } catch {
            // if removing the hold fails, refetch the holds to repopulate the ui list and exit the function
            await fetchCurrentHolds();
            return;
          }
        }
      }
    }

    props.refreshCb({
      inventoryWasEmptied: updatedQty === 0,
      newQuantity: updatedQty ?? undefined,
      inventoryId: updatedInventoryToModify?.inventoryId,
      newDate: inventoryDate ? inventoryDate.toDate() : undefined
    });
    updateQuantity(null);
    updateReasonCode("Counted");
    setInventoryDate(null);
    setTransferOutLocation(null);
    if (onClose && window.location.pathname.includes("inventory")) {
      closeDialog();
    } else if (!isCycleCountsPage) {
      dispatch(clearInventoryToModify());
    }
  };

  const handleClearInventoryClick = (): Promise<void> | null => {
    mixpanelTrack({
      trackedPageName: trackedModalName,
      type: "Button Click",
      label: "Clear Inventory"
    });
    setConfirmationModalOpen(true);
    return null;
  };

  const handleAddHoldToDropdownList = (newHoldType: HoldType) => {
    mixpanelTrack({
      trackedPageName: trackedModalName,
      type: "Select Option",
      label: "Add Hold to List",
      eventProperties: {
        "Reason Code": newHoldType.reasonCode
      }
    });
    setCurrentHoldTypes([...currentHoldTypes, newHoldType]);
    setAreHoldFieldsDirty(true);
  };

  const handleRemoveHoldFromDropdownList = (holdToRemove: HoldType) => {
    if (
      holdTypeCheck(
        holdToRemove,
        errorToast,
        isExpirationDate ? dayjs(inventoryDate?.toDate()) : undefined
      )
    )
      return;

    mixpanelTrack({
      trackedPageName: trackedModalName,
      type: "Button Click",
      label: "Remove Hold from List",
      eventProperties: {
        "Reason Code": holdToRemove.reasonCode
      }
    });

    setCurrentHoldTypes(
      currentHoldTypes.filter(
        (hold: { reasonCode: InventoryHoldReasonCode }) =>
          hold.reasonCode !== holdToRemove.reasonCode
      )
    );

    setAreHoldFieldsDirty(true);
  };

  const quantityTextField = (
    <TextField
      style={{ width: "100%" }}
      id="quantity"
      label={t("quantity")}
      type="text"
      variant="outlined"
      value={quantityToDisplay}
      onChange={handleQuantityChange}
      onBlur={() => {
        if (quantity !== null) {
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Input Change",
            label: "Manual Quantity Input"
          });
        }
      }}
      disabled={
        disabled || !updatedInventoryToModify || !updatedInventoryToModify.count
      }
      error={!quantityToDisplay && quantityToDisplay !== 0}
      inputProps={{
        role: "spinbutton",
        sx: {
          fontSize: "4rem",
          padding: "35px 10px 35px 18px"
        }
      }}
      InputLabelProps={{
        shrink: true
      }}
    />
  );

  const isDecreaseQuantityDisabled =
    disabled || quantity === null || quantity <= 0;

  const quantityButtons = (
    <Stack gap={1.5} direction="column" justifyContent="center">
      <SquareIconButton
        aria-label="increase quantity"
        onClick={(): void => {
          const newQty = quantity ? quantity + 1 : 1;
          if (newQty > QUANTITY_MAX_VALUE) {
            return;
          }
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Button Click",
            label: "Increase Quantity",
            eventProperties: {
              "Previous Quantity": quantity,
              "New Quantity": newQty
            }
          });
          setInventoryDirty();
          updateQuantity(newQty);
        }}
        disabled={
          disabled || (quantity !== null && quantity >= QUANTITY_MAX_VALUE)
        }
        size="large"
      >
        <ExpandLessIcon
          style={{
            fontSize: 27
          }}
        />
      </SquareIconButton>

      <SquareIconButton
        aria-label="decrease quantity"
        onClick={(): void => {
          if (isDecreaseQuantityDisabled) return;

          const newQty = quantity - 1;

          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Button Click",
            label: "Decrease Quantity",
            eventProperties: {
              "Previous Quantity": quantity,
              "New Quantity": newQty
            }
          });

          setInventoryDirty();
          updateQuantity(newQty);
        }}
        disabled={isDecreaseQuantityDisabled}
        size="large"
      >
        <ExpandMoreIcon
          style={{
            fontSize: 27
          }}
        />
      </SquareIconButton>
    </Stack>
  );

  const reasonCodeField = (
    <TextField
      id="reasonCode"
      label={t("reason code")}
      type="string"
      select
      fullWidth
      variant="outlined"
      value={reasonCode || ""}
      InputLabelProps={{
        shrink: true
      }}
      inputProps={{
        sx: { fontSize: "1rem" }
      }}
      SelectProps={{
        onChange: handleReasonCodeChange
      }}
      disabled={disabled}
    >
      {generateInventoryReasonCodeOptions(clientFCReasonCodes).map(
        (choiceObj, i, arr) => (
          <MenuItem
            key={`uom-option-${choiceObj.value}`}
            className={`uom-option-${i}`}
            sx={{
              padding: 1,
              fontSize: "24px",
              borderBottomColor: "gray.light",
              borderBottom: i === arr.length - 1 ? "none" : `1px solid`
            }}
            value={choiceObj.value}
          >
            {choiceObj.label}
          </MenuItem>
        )
      )}
    </TextField>
  );

  const currentHoldTextField = (
    <List sx={{ width: "100%" }}>
      {currentHoldTypes.map((currentHold) => (
        <ListItem
          aria-label={currentHold.reasonCode}
          data-reasoncode={currentHold.reasonCode}
          key={currentHold.reasonCode}
          onClick={() => {
            handleRemoveHoldFromDropdownList(currentHold);
          }}
          secondaryAction={
            <IconButton
              edge="end"
              aria-label="delete"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveHoldFromDropdownList(currentHold);
              }}
            >
              <HighlightOffIcon />
            </IconButton>
          }
          sx={{
            border: "1px solid",
            borderRadius: "4px",
            padding: 1,
            mb: 1
          }}
        >
          <ListItemText
            primary={
              <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                {t(currentHold.reasonCode)}
              </Typography>
            }
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          />
        </ListItem>
      ))}
    </List>
  );

  const filteredHoldTextField = (
    <TextField
      id="filteredHoldType"
      label={t("add hold")}
      type="string"
      select
      fullWidth
      variant="outlined"
      value=""
      InputLabelProps={{
        shrink: true
      }}
      inputProps={{
        sx: { fontSize: "1rem" }
      }}
      disabled={disabled}
    >
      {holdTypes.map((item) => (
        <MenuItem
          value={item.reasonCode.toLocaleLowerCase()}
          key={item.reasonCode}
          onClick={() => {
            handleAddHoldToDropdownList(item);
          }}
          data-reasoncode={item.reasonCode}
          style={{ padding: "1.5em 0 1.5em 0.5em" }}
          disabled={currentHoldTypes.some(
            (h) => h.reasonCode === item.reasonCode
          )}
        >
          {t(item.reasonCode)}
          <IconButton onClick={() => handleAddHoldToDropdownList(item)}>
            <AddCircleOutlineIcon />
          </IconButton>
        </MenuItem>
      ))}
    </TextField>
  );

  const unitOfMeasureField = (
    <TextField
      sx={{ flex: 1 }}
      id="unitOfMeasure"
      label={t("uom")}
      type="string"
      variant="outlined"
      value={unitOfMeasure || ""}
      disabled
      InputLabelProps={{
        shrink: true
      }}
      inputProps={{
        sx: {
          fontSize: 50,
          padding: "35px 10px 35px 18px",
          p: "35px 5px"
        }
      }}
    />
  );

  const inventoryHoldField = (
    <Button
      onClick={() => {
        mixpanelTrack({
          trackedPageName: trackedModalName,
          type: "Button Click",
          label: expanded ? "Hide Inventory Hold" : "Show Inventory Hold"
        });
        setExpanded(!expanded);
      }}
      color="secondary"
    >
      {expanded ? (
        <>
          <Typography variant="overline" fontSize="18px" textTransform="none">
            {t("hide inventory hold")}
          </Typography>
          <ExpandLessIcon key="expand-less-icon" />
        </>
      ) : (
        <>
          <Typography variant="overline" fontSize="18px" textTransform="none">
            {t("inventory hold")}
          </Typography>
          <ExpandMoreIcon key="expand-more-icon" />
        </>
      )}
    </Button>
  );

  const isLotNumberRequiredAndEmpty =
    selectedVariant?.isLotNumberRequired && !updatedLotNumber;
  const hasLotNumberBeenCleared =
    !!updatedInventoryToModify?.lotNumber && !updatedLotNumber;
  const isLotNumberError =
    quantity !== 0 && (isLotNumberRequiredAndEmpty || hasLotNumberBeenCleared);

  const lotNumberField = (
    <TextField
      fullWidth
      variant="outlined"
      type="text"
      label={t("lot number")}
      value={updatedLotNumber || ""}
      onChange={(e) => {
        setInventoryDirty();
        setUpdatedLotNumber(e.target.value);
      }}
      onBlur={() => {
        if (updatedLotNumber) {
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Input Change",
            label: "Lot Number"
          });
        }
      }}
      error={isLotNumberError}
      slotProps={{
        htmlInput: { maxLength: LOT_NUMBER_MAX_LENGTH }
      }}
    />
  );

  const transferOutLocationField = (
    <TextField
      fullWidth
      variant="outlined"
      type="text"
      label={t("transfer out location")}
      placeholder={t("scan or type location")}
      slotProps={{
        inputLabel: {
          shrink: true
        }
      }}
      value={transferOutLocation}
      onChange={(e) => {
        setInventoryDirty();
        setTransferOutLocation(e.target.value);
      }}
      onBlur={() => {
        if (transferOutLocation) {
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Input Change",
            label: "Transfer Out Location"
          });
        }
      }}
    />
  );

  const dateLabel = inventoryDateLabel(inv_inventoryDateLabel);
  const capitalizedDateLabelForTracking =
    dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);

  const inventoryDatePicker = (
    <DesktopDatePicker
      slotProps={{
        textField: {
          InputLabelProps: {
            shrink: true
          },
          onBlur: (e) => {
            mixpanelTrack({
              trackedPageName: trackedModalName,
              type: "Input Change",
              label: `${capitalizedDateLabelForTracking} (Manual Input)`,
              eventProperties: {
                Date: e.target.value
              }
            });
          },
          label: t(inventoryDateLabel(inv_inventoryDateLabel)),
          variant: "outlined",
          fullWidth: true,
          error: isDateFieldError
        }
      }}
      label={t(inventoryDateLabel(inv_inventoryDateLabel))}
      value={
        inventoryDate?.isValid() ? dayjs(formatDayjsMaybe(inventoryDate)) : null
      }
      minDate={getMinInventoryExpirationDate(inv_inventoryDateLabel)}
      format={globalDateFormat}
      onChange={(date) => {
        setInventoryDirty();
        if (
          isExpirationValid(
            inv_inventoryDateRequired || selectedVariant?.isExpirationRequired,
            date
          )
        ) {
          setIsAdjustBtnDisabled(false);
        } else {
          setIsAdjustBtnDisabled(true);
        }
        setInventoryDate(date);
      }}
      onAccept={(date) => {
        mixpanelTrack({
          trackedPageName: trackedModalName,
          type: "Select Option",
          label: `${capitalizedDateLabelForTracking} (Calendar Select)`,
          eventProperties: {
            Date: date ? date.format(globalDateFormat) : null
          }
        });
      }}
      onOpen={() => {
        mixpanelTrack({
          trackedPageName: trackedModalName,
          type: "Button Click",
          label: `Open ${capitalizedDateLabelForTracking} Calendar`
        });
      }}
      disabled={disabled}
    />
  );

  // default to Adjust text.
  // toggle between verify and adjust if counting-task is blind and if inventory state has been udpated
  let adjustmentButtonText = t("adjust");
  if (window.location.pathname.includes("autostore-cyclecountv2")) {
    if (!isCycleCountBlind && !hasInventoryAdjusted)
      adjustmentButtonText = t("verify");
  }

  const AdjustInventoryButton = (
    <ProgressButton
      sx={{ backgroundColor: "primary.main", color: "white" }}
      buttonSize="medium"
      emphasis="high"
      responsive
      fullWidth
      startIcon={<ImportExportIcon />}
      disabled={isAdjustBtnDisabled || disabled || !isInputDateValid}
      onClick={() => handleConfirmClick()}
    >
      {adjustmentButtonText}
    </ProgressButton>
  );

  const ClearInventoryButton = (
    <ProgressButton
      buttonSize="medium"
      emphasis="high"
      responsive
      fullWidth
      startIcon={<HighlightOffIcon />}
      onClick={(): Promise<void> | null => handleClearInventoryClick()}
      disabled={disabled}
      color="error"
    >
      {t("clear inventory")}
    </ProgressButton>
  );

  const ConfirmButton = (
    <ProgressButton
      id="confirm-button"
      color="primary"
      buttonSize="medium"
      emphasis="high"
      responsive
      fullWidth
      startIcon={<CheckIcon />}
      onClick={(): Promise<void> | null => handleConfirmClick()}
      disabled={
        disabled ||
        isDateFieldError ||
        typeof quantity !== "number" ||
        isLotNumberError
      }
    >
      {t(confirmText)}
    </ProgressButton>
  );

  const confirmationPrompt = `${t("are you sure you want to clear all")} ${
    updatedInventoryToModify?.count?.value || ""
  } ${updatedInventoryToModify?.count?.units || ""} ${t("of")} ${
    selectedVariant?.productName ?? ""
  } ${t("out of bin")} ${invToModify?.locationName || ""}?`;

  const getModalHeaderText = (): string | null => {
    // When the modal first opens bin info is sometimes still being fetched
    // so we show a blank string to prevent showing one thing and then it changing
    if (
      !numberOfCompartments ||
      !updatedInventoryToModify?.autostoreBinNumber
    ) {
      return null;
    }

    const binTranslation = t("bin");

    return numberOfCompartments === 1 ||
      !updatedInventoryToModify?.autostoreCompartmentNumber
      ? `${binTranslation} ${updatedInventoryToModify.autostoreBinNumber}`
      : `${binTranslation} ${updatedInventoryToModify.autostoreBinNumber} - ${updatedInventoryToModify.autostoreCompartmentNumber}`;
  };

  const headerText = getModalHeaderText();

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        mixpanelTrackModalDismiss(trackedModalName, reason);
        closeDialog();
      }}
      maxWidth="tablet"
      aria-labelledby="InventoryAdjustDialog-header-text"
    >
      <DialogTitle id="InventoryAdjustDialog-header-text">
        {headerText ?? (
          <Skeleton aria-label="loading header" variant="text" width="30%" />
        )}
      </DialogTitle>
      {onClose && (
        <Box sx={{ position: "absolute", right: 5, top: 15 }}>
          <IconButton
            onClick={() => {
              mixpanelTrack({
                trackedPageName: trackedModalName,
                type: "Button Click",
                label: "Close"
              });
              closeDialog();
            }}
            aria-label="close"
          >
            <CloseIcon
              sx={{
                color: "primary.dark"
              }}
            />
          </IconButton>
        </Box>
      )}

      <DialogContent>
        <Stack gap={2}>
          <Stack>
            <Typography variant="overline">{t("name")}</Typography>
            <Typography role="heading" variant="h5">
              {updatedInventoryLoading ? (
                <Skeleton aria-label="loading product name" width="50%" />
              ) : (
                (updatedInventoryToModify?.productName ?? "")
              )}
            </Typography>
          </Stack>
          <Stack direction="row" gap={2} alignItems="center">
            <Stack direction="row" gap={1} flex={1}>
              {quantityTextField}
              {!isMobile && invToModify?.count && quantityButtons}
            </Stack>
            {children && <Box flex={1}>{children}</Box>}
            {!children && unitOfMeasureField}
          </Stack>
          <Stack direction="row" gap={2}>
            {lotNumberField}
            {inventoryDatePicker}
          </Stack>
          {reasonCodeField}
          {reasonCode.toLowerCase() === "transfer" && transferOutLocationField}
          {isAdjustOnMain && (
            <Stack direction="row" gap={2}>
              {AdjustInventoryButton}
              {ClearInventoryButton}
            </Stack>
          )}
          {!isAdjustOnMain && (
            <>
              <Divider />
              <Box justifyContent="flex-start" width="fit-content">
                {inventoryHoldField}
                <Collapse in={expanded}>
                  <Stack>
                    {currentHoldTextField}
                    {filteredHoldTextField}
                  </Stack>
                </Collapse>
              </Box>
              {ConfirmButton}
            </>
          )}
        </Stack>
      </DialogContent>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        confirmCb={async () => {
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Button Click",
            label: "Confirm Clear Inventory"
          });
          await handleConfirmClick(true);
          setConfirmationModalOpen(false);
          setInventoryDirty();
        }}
        closeCb={() => {
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Button Click",
            label: "Cancel Clear Inventory"
          });
          setConfirmationModalOpen(false);
        }}
        modalTitle={t("confirm clear")}
        modalText={confirmationPrompt}
        labelledById="confirm-clear-modal-title"
      />
    </Dialog>
  );
}

export default connector(InventoryAdjustDialog);
